// fMP4 (fragmented, as mux.js emits) → progressive MP4 (single moov + one mdat).
// Pure JS, no deps. Fragmented MP4 plays in Chrome/VLC but NOT in QuickTime / Windows
// Media Player (they want a progressive layout: one complete moov with sample tables);
// iOS/Android use native progressive muxers, so this brings web to parity.
//
// mux.js emits, per track, a separate `moof`+`mdat` pair. We read each track's samples
// from its `trun`, rebuild the moov's per-track `stbl` (stts/ctts/stsz/stsc/stco/stss),
// drop `mvex`, and concatenate the sample data into one `mdat`. Validated with ffprobe/
// ffmpeg (0 moof, clean full decode, correct frame counts).

interface Box {
  type: string;
  start: number;
  end: number;
  hdr: number;
  children?: Box[];
  data?: Uint8Array;
  raw?: Uint8Array;
}
interface Sample {
  dur: number;
  size: number;
  cto: number;
  sync: boolean;
}

const CONTAINERS = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl', 'mvex', 'moof', 'traf', 'dinf', 'edts']);
const u32 = (b: Uint8Array, p: number) => b[p] * 0x1000000 + (b[p + 1] << 16) + (b[p + 2] << 8) + b[p + 3];
const u64 = (b: Uint8Array, p: number) => Number((BigInt(u32(b, p)) << 32n) | BigInt(u32(b, p + 4)));
const asc = (b: Uint8Array, p: number, n: number) => String.fromCharCode(...b.slice(p, p + n));
const strBytes = (s: string) => Uint8Array.from(s, (c) => c.charCodeAt(0));

function concat(arrs: Uint8Array[]): Uint8Array {
  let n = 0;
  for (const a of arrs) n += a.length;
  const o = new Uint8Array(n);
  let p = 0;
  for (const a of arrs) {
    o.set(a, p);
    p += a.length;
  }
  return o;
}
function u32b(n: number): Uint8Array {
  const a = new Uint8Array(4);
  new DataView(a.buffer).setUint32(0, n >>> 0);
  return a;
}
function box(type: string, ...payloads: Uint8Array[]): Uint8Array {
  const body = concat(payloads);
  const out = new Uint8Array(8 + body.length);
  new DataView(out.buffer).setUint32(0, out.length);
  out.set(strBytes(type), 4);
  out.set(body, 8);
  return out;
}
function fullbox(type: string, version: number, flags: number, ...payloads: Uint8Array[]): Uint8Array {
  const head = new Uint8Array(4);
  head[0] = version;
  head[1] = (flags >> 16) & 0xff;
  head[2] = (flags >> 8) & 0xff;
  head[3] = flags & 0xff;
  return box(type, head, ...payloads);
}
function patchU32(buf: Uint8Array, pos: number, val: number) {
  new DataView(buf.buffer, buf.byteOffset).setUint32(pos, val >>> 0);
}
function patchU64(d: Uint8Array, pos: number, val: number) {
  const dv = new DataView(d.buffer, d.byteOffset);
  dv.setUint32(pos, Math.floor(val / 0x100000000));
  dv.setUint32(pos + 4, val >>> 0);
}

function parseBoxes(b: Uint8Array, start: number, end: number): Box[] {
  const boxes: Box[] = [];
  let p = start;
  while (p + 8 <= end) {
    let size = u32(b, p);
    let hdr = 8;
    const type = asc(b, p + 4, 4);
    if (size === 1) {
      size = u64(b, p + 8);
      hdr = 16;
    } else if (size === 0) size = end - p;
    if (size < hdr || p + size > end) break;
    const bx: Box = { type, start: p, end: p + size, hdr };
    if (CONTAINERS.has(type)) bx.children = parseBoxes(b, p + hdr, p + size);
    else bx.data = b.slice(p + hdr, p + size);
    boxes.push(bx);
    p += size;
  }
  return boxes;
}
const find = (boxes: Box[], type: string) => boxes.find((x) => x.type === type);
const findAll = (boxes: Box[], type: string) => boxes.filter((x) => x.type === type);

function serialize(node: Box): Uint8Array {
  if (node.raw) return node.raw;
  if (node.children) return box(node.type, concat(node.children.map(serialize)));
  return box(node.type, node.data ?? new Uint8Array(0));
}

function parseTrex(mvex: Box | undefined): Record<number, { dur: number; size: number; flags: number }> {
  const map: Record<number, { dur: number; size: number; flags: number }> = {};
  if (!mvex?.children) return map;
  for (const t of findAll(mvex.children, 'trex')) {
    const d = t.data!;
    map[u32(d, 4)] = { dur: u32(d, 12), size: u32(d, 16), flags: u32(d, 20) };
  }
  return map;
}

function parseTraf(traf: Box, trex: Record<number, { dur: number; size: number; flags: number }>): { trackId: number; samples: Sample[] } {
  const tfhd = find(traf.children!, 'tfhd')!.data!;
  const tf = u32(tfhd, 0) & 0xffffff;
  const trackId = u32(tfhd, 4);
  let p = 8;
  let defDur: number | undefined, defSize: number | undefined, defFlags: number | undefined;
  if (tf & 0x000001) p += 8;
  if (tf & 0x000002) p += 4;
  if (tf & 0x000008) { defDur = u32(tfhd, p); p += 4; }
  if (tf & 0x000010) { defSize = u32(tfhd, p); p += 4; }
  if (tf & 0x000020) { defFlags = u32(tfhd, p); p += 4; }
  const tx = trex[trackId] ?? { dur: 0, size: 0, flags: 0 };
  const dDur = defDur ?? tx.dur, dSize = defSize ?? tx.size, dFlags = defFlags ?? tx.flags;

  const trun = find(traf.children!, 'trun')!.data!;
  const rf = u32(trun, 0) & 0xffffff;
  const count = u32(trun, 4);
  let q = 8;
  if (rf & 0x000001) q += 4;
  let firstFlags: number | null = null;
  if (rf & 0x000004) { firstFlags = u32(trun, q); q += 4; }
  const samples: Sample[] = [];
  for (let i = 0; i < count; i++) {
    let dur = dDur, size = dSize, flags = dFlags, cto = 0;
    if (rf & 0x000100) { dur = u32(trun, q); q += 4; }
    if (rf & 0x000200) { size = u32(trun, q); q += 4; }
    if (rf & 0x000400) { flags = u32(trun, q); q += 4; }
    if (rf & 0x000800) { cto = u32(trun, q); q += 4; }
    if (i === 0 && firstFlags !== null) flags = firstFlags;
    samples.push({ dur, size, cto, sync: (flags & 0x00010000) === 0 });
  }
  return { trackId, samples };
}

function buildStts(samples: Sample[]): Uint8Array {
  const runs: { count: number; delta: number }[] = [];
  for (const s of samples) {
    const last = runs[runs.length - 1];
    if (last && last.delta === s.dur) last.count++;
    else runs.push({ count: 1, delta: s.dur });
  }
  const body = [u32b(runs.length)];
  for (const r of runs) body.push(u32b(r.count), u32b(r.delta));
  return fullbox('stts', 0, 0, concat(body));
}
function buildCtts(samples: Sample[]): Uint8Array | null {
  if (!samples.some((s) => s.cto !== 0)) return null;
  const runs: { count: number; off: number }[] = [];
  for (const s of samples) {
    const last = runs[runs.length - 1];
    if (last && last.off === s.cto) last.count++;
    else runs.push({ count: 1, off: s.cto });
  }
  const body = [u32b(runs.length)];
  for (const r of runs) body.push(u32b(r.count), u32b(r.off));
  return fullbox('ctts', 0, 0, concat(body));
}
function buildStsz(samples: Sample[]): Uint8Array {
  const body = [u32b(0), u32b(samples.length)];
  for (const s of samples) body.push(u32b(s.size));
  return fullbox('stsz', 0, 0, concat(body));
}
const buildStsc = (n: number) => fullbox('stsc', 0, 0, concat([u32b(1), u32b(1), u32b(n), u32b(1)]));
const buildStco = () => fullbox('stco', 0, 0, concat([u32b(1), u32b(0)])); // offset patched later
function buildStss(samples: Sample[]): Uint8Array | null {
  const idx: number[] = [];
  samples.forEach((s, i) => { if (s.sync) idx.push(i + 1); });
  if (idx.length === 0 || idx.length === samples.length) return null;
  const body = [u32b(idx.length)];
  for (const i of idx) body.push(u32b(i));
  return fullbox('stss', 0, 0, concat(body));
}

function patchStcoOffsets(moovBytes: Uint8Array, vals: number[]) {
  let idx = 0;
  const walk = (start: number, end: number) => {
    let p = start;
    while (p + 8 <= end) {
      let size = u32(moovBytes, p);
      let hdr = 8;
      const type = asc(moovBytes, p + 4, 4);
      if (size === 1) { size = u64(moovBytes, p + 8); hdr = 16; } else if (size === 0) size = end - p;
      if (size < hdr) break;
      if (type === 'stco') patchU32(moovBytes, p + hdr + 8, vals[idx++]);
      else if (CONTAINERS.has(type)) walk(p + hdr, p + size);
      p += size;
    }
  };
  walk(0, moovBytes.length);
}

/** Convert a fragmented MP4 (mux.js output) into a progressive MP4. */
export function fmp4ToProgressive(fmp4: Uint8Array): Uint8Array {
  const b = fmp4;
  const top = parseBoxes(b, 0, b.length);
  const ftyp = find(top, 'ftyp');
  const moov = find(top, 'moov');
  const moofs = findAll(top, 'moof');
  const mdats = findAll(top, 'mdat');
  if (!ftyp || !moov || moofs.length === 0 || mdats.length !== moofs.length) {
    throw new Error('unexpected fmp4 layout');
  }
  const trex = parseTrex(find(moov.children!, 'mvex'));

  const perTrack: Record<number, { samples: Sample[]; data: Uint8Array }> = {};
  for (let i = 0; i < moofs.length; i++) {
    const traf = find(moofs[i].children!, 'traf')!;
    const { trackId, samples } = parseTraf(traf, trex);
    perTrack[trackId] = { samples, data: mdats[i].data! };
  }

  // Movie timescale (from mvhd): tkhd/mvhd durations are expressed in THIS timescale,
  // not the per-track (mdhd) timescale. mux.js leaves mvhd.duration = 0xFFFFFFFF (a
  // fragmented placeholder) — QuickTime reads mdhd so it plays fine, but Windows Media
  // Player reads mvhd and shows a bogus ~13h length. Patch both.
  const mvhd = find(moov.children!, 'mvhd')!;
  const mvd = mvhd.data!;
  const movieTimescale = mvd[0] === 1 ? u32(mvd, 20) : u32(mvd, 12);
  let maxMovieDur = 0;

  const trackOrder: { data: Uint8Array }[] = [];
  for (const trak of findAll(moov.children!, 'trak')) {
    const tkhd = find(trak.children!, 'tkhd')!;
    const mdia = find(trak.children!, 'mdia')!;
    const mdhd = find(mdia.children!, 'mdhd')!;
    const stbl = find(find(mdia.children!, 'minf')!.children!, 'stbl')!;
    const stsd = find(stbl.children!, 'stsd')!;
    const tkd = tkhd.data!;
    const trackId = tkd[0] === 1 ? u32(tkd, 20) : u32(tkd, 12);
    const pt = perTrack[trackId];
    if (!pt) continue;
    const totalDur = pt.samples.reduce((a, s) => a + s.dur, 0); // in track (mdhd) timescale
    const md = mdhd.data!;
    const trackTimescale = md[0] === 1 ? u32(md, 20) : u32(md, 12);
    if (md[0] === 1) patchU64(md, 24, totalDur);
    else patchU32(md, 16, totalDur);
    // tkhd duration is in MOVIE timescale → convert.
    const movieDur = Math.round((totalDur * movieTimescale) / (trackTimescale || movieTimescale));
    if (tkd[0] === 1) patchU64(tkd, 28, movieDur);
    else patchU32(tkd, 20, movieDur);
    if (movieDur > maxMovieDur) maxMovieDur = movieDur;

    const newStbl: Uint8Array[] = [serialize(stsd), buildStts(pt.samples)];
    const ctts = buildCtts(pt.samples);
    if (ctts) newStbl.push(ctts);
    newStbl.push(buildStsz(pt.samples), buildStsc(pt.samples.length), buildStco());
    const stss = buildStss(pt.samples);
    if (stss) newStbl.push(stss);
    stbl.children = newStbl.map((raw) => ({ type: 'raw', start: 0, end: 0, hdr: 0, raw }));
    trackOrder.push({ data: pt.data });
  }
  // Patch mvhd duration (movie timescale) — replaces mux.js's 0xFFFFFFFF placeholder.
  if (mvd[0] === 1) patchU64(mvd, 24, maxMovieDur);
  else patchU32(mvd, 16, maxMovieDur);
  moov.children = moov.children!.filter((x) => x.type !== 'mvex');

  const ftypBytes = serialize(ftyp);
  const moovBytes = serialize(moov);
  const mdatDataStart = ftypBytes.length + moovBytes.length + 8;
  let running = mdatDataStart;
  const stcoVals: number[] = [];
  const mdatParts: Uint8Array[] = [];
  for (const t of trackOrder) {
    stcoVals.push(running);
    running += t.data.length;
    mdatParts.push(t.data);
  }
  patchStcoOffsets(moovBytes, stcoVals);

  const mdatPayload = concat(mdatParts);
  const mdatBox = new Uint8Array(8 + mdatPayload.length);
  new DataView(mdatBox.buffer).setUint32(0, mdatBox.length);
  mdatBox.set(strBytes('mdat'), 4);
  mdatBox.set(mdatPayload, 8);

  return concat([ftypBytes, moovBytes, mdatBox]);
}
